import { useEffect, useMemo, useState } from "react";
import { ArrowDown, ArrowUp, ArrowUpDown, RotateCcw, FlaskConical } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";

/* ---------- formatting ---------- */
export const inr = (n, d = 0) =>
  "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: d, minimumFractionDigits: d });

export const inrCompact = (n) => {
  const v = Number(n || 0);
  const a = Math.abs(v);
  if (a >= 1e7) return `₹${(v / 1e7).toFixed(2)} Cr`;
  if (a >= 1e5) return `₹${(v / 1e5).toFixed(2)} L`;
  if (a >= 1e3) return `₹${(v / 1e3).toFixed(1)}K`;
  return inr(v);
};

export const num = (n, d = 0) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: d });
export const pct = (n, d = 1) => `${Number(n || 0).toFixed(d)}%`;

export const fmtDate = (iso, withTime = false) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-IN", withTime
    ? { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }
    : { day: "2-digit", month: "short", year: "2-digit" });
};

export const hoursLabel = (h) => {
  const v = Number(h || 0);
  if (v >= 48) return `${(v / 24).toFixed(1)}d`;
  return `${Math.round(v)}h`;
};

/* ---------- status pills ---------- */
export const STATUS_STYLE = {
  manifested: "bg-slate-500/15 text-slate-600 dark:text-slate-300 border-slate-500/30",
  dispatched: "bg-indigo-500/15 text-indigo-600 dark:text-indigo-300 border-indigo-500/30",
  in_transit: "bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-500/30",
  out_for_delivery: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30",
  delivered: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30",
  rto: "bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/30",
};
export const STATUS_LABEL = {
  manifested: "Manifested", dispatched: "Dispatched", in_transit: "In-Transit",
  out_for_delivery: "Out for Delivery", delivered: "Delivered", rto: "RTO",
};
export const STATUSES = Object.keys(STATUS_LABEL);

export function Pill({ className = "", children, testid }) {
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1 border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide whitespace-nowrap ${className}`}>
      {children}
    </span>
  );
}

export function StatusPill({ status }) {
  return <Pill className={STATUS_STYLE[status] || STATUS_STYLE.manifested}>{STATUS_LABEL[status] || status}</Pill>;
}

const RISK_STYLE = {
  high: "bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/30",
  medium: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30",
  low: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/20",
};
export function RiskPill({ risk }) {
  if (!risk) return <span className="text-muted-foreground text-xs">—</span>;
  return <Pill className={RISK_STYLE[risk.level]}>{risk.score} · {risk.level}</Pill>;
}

const SEV_STYLE = {
  critical: "bg-rose-600 text-white border-rose-600",
  high: "bg-orange-500/15 text-orange-700 dark:text-orange-300 border-orange-500/40",
  medium: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30",
};
export const EXC_LABEL = { lost: "Lost (no scan 7d+)", stuck: "Stuck (no scan 72h+)", delayed: "SLA breached" };
export function ExceptionPill({ exc }) {
  if (!exc) return null;
  return <Pill className={SEV_STYLE[exc.severity]}>{exc.type}</Pill>;
}

/* ---------- layout ---------- */
export function AggHeader({ title, subtitle, icon: Icon, actions, testid, kicker = "3PL Aggregator" }) {
  return (
    <div data-testid={testid} className="bg-[#0B1220] text-white border border-[#1c2740] px-4 sm:px-5 py-4 flex items-start sm:items-center justify-between gap-3 flex-wrap">
      <div className="min-w-0">
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-orange-400 flex items-center gap-2">
          {kicker} <SampleBadge />
        </div>
        <h1 className="font-heading font-black text-xl sm:text-2xl tracking-tight flex items-center gap-2 mt-0.5">
          {Icon && <Icon className="h-5 w-5 text-orange-400 shrink-0" />} {title}
        </h1>
        {subtitle && <p className="text-xs sm:text-sm text-slate-400 mt-0.5">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
    </div>
  );
}

export function SampleBadge() {
  return (
    <span className="inline-flex items-center gap-1 bg-orange-500/15 border border-orange-500/40 text-orange-300 px-1.5 py-0.5 text-[9px] tracking-wider" title="Sample data – carrier events, re-routes and claims are recorded in-app only">
      <FlaskConical className="h-2.5 w-2.5" /> Sample data
    </span>
  );
}

export function DarkSelect({ value, onChange, children, testid, className = "" }) {
  return (
    <select
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`h-8 bg-[#111a2e] border border-[#26324d] text-slate-100 text-xs px-2 focus:outline-none focus:border-orange-400 ${className}`}
    >
      {children}
    </select>
  );
}

export function FieldSelect({ value, onChange, children, testid, className = "" }) {
  return (
    <select
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`h-9 bg-background border border-input text-sm px-2 focus:outline-none focus:ring-1 focus:ring-primary ${className}`}
    >
      {children}
    </select>
  );
}

export const PERIODS = [[7, "Last 7 days"], [30, "Last 30 days"], [90, "Last 90 days"], [0, "All time"]];

export function PeriodSelect({ value, onChange, testid = "agg-period" }) {
  return (
    <DarkSelect value={value} onChange={(v) => onChange(Number(v))} testid={testid}>
      {PERIODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </DarkSelect>
  );
}

export function ResetSampleButton({ onDone, endpoint = "/agg/seed/reset", label = "3PL Aggregator" }) {
  const [busy, setBusy] = useState(false);
  return (
    <button
      data-testid="agg-reset-sample"
      disabled={busy}
      onClick={async () => {
        if (!window.confirm(`Reset all ${label} data to fresh sample data? Your edits in this module will be lost.`)) return;
        setBusy(true);
        try { await api.post(endpoint); toast.success("Sample data regenerated"); onDone?.(); }
        catch (e) { toast.error(apiErr(e)); }
        finally { setBusy(false); }
      }}
      className="h-8 px-2.5 border border-[#26324d] text-slate-300 hover:text-white hover:border-orange-400 text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
    >
      <RotateCcw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} /> Reset sample
    </button>
  );
}

export function Kpi({ label, value, sub, icon: Icon, accent = "text-primary", delta, testid, children }) {
  return (
    <div data-testid={testid} className="border border-border bg-card p-4 flex flex-col gap-1 min-w-0">
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground truncate">{label}</div>
        {Icon && <Icon className={`h-4 w-4 shrink-0 ${accent}`} />}
      </div>
      <div className="font-heading font-black text-2xl tracking-tight font-mono-num truncate">{value}</div>
      <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
        {delta !== undefined && delta !== null && (
          <span className={`font-bold ${delta >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"}`}>
            {delta >= 0 ? "▲" : "▼"} {Math.abs(delta).toFixed(1)}%
          </span>
        )}
        {sub}
      </div>
      {children}
    </div>
  );
}

export function Panel({ title, subtitle, actions, children, testid, className = "", bodyClass = "" }) {
  return (
    <section data-testid={testid} className={`border border-border bg-card min-w-0 ${className}`}>
      {(title || actions) && (
        <div className="px-4 py-3 border-b border-border flex items-center justify-between gap-2 flex-wrap">
          <div className="min-w-0">
            <h2 className="font-heading font-bold text-sm uppercase tracking-wide">{title}</h2>
            {subtitle && <p className="text-[11px] text-muted-foreground mt-0.5">{subtitle}</p>}
          </div>
          {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
        </div>
      )}
      <div className={bodyClass}>{children}</div>
    </section>
  );
}

/* ---------- client-side sort ---------- */
export function useSort(rows, initialKey, initialDir = "desc") {
  const [key, setKey] = useState(initialKey);
  const [dir, setDir] = useState(initialDir);
  const sorted = useMemo(() => {
    const list = [...(rows || [])];
    if (!key) return list;
    const get = (r) => key.split(".").reduce((o, k) => (o == null ? o : o[k]), r);
    list.sort((a, b) => {
      const x = get(a), y = get(b);
      if (x == null && y == null) return 0;
      if (x == null) return 1;
      if (y == null) return -1;
      const c = typeof x === "number" && typeof y === "number" ? x - y : String(x).localeCompare(String(y));
      return dir === "asc" ? c : -c;
    });
    return list;
  }, [rows, key, dir]);
  const toggle = (k) => {
    if (k === key) setDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setKey(k); setDir("desc"); }
  };
  return { sorted, key, dir, toggle };
}

export function SortTh({ k, label, sort, align = "left", className = "" }) {
  const active = sort.key === k;
  const Icon = !active ? ArrowUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <th className={`px-3 py-2 font-bold whitespace-nowrap ${align === "right" ? "text-right" : "text-left"} ${className}`}>
      <button
        type="button"
        data-testid={`sort-${k}`}
        onClick={() => sort.toggle(k)}
        className={`inline-flex items-center gap-1 uppercase tracking-wide hover:text-foreground ${active ? "text-foreground" : ""} ${align === "right" ? "flex-row-reverse" : ""}`}
      >
        {label} <Icon className="h-3 w-3 opacity-70" />
      </button>
    </th>
  );
}

export const TH_ROW = "bg-muted/70 text-[10px] uppercase tracking-wide text-muted-foreground";
export const TD = "px-3 py-2 whitespace-nowrap";

export function TableFilter({ value, onChange, placeholder = "Filter…", testid }) {
  return (
    <input
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="h-8 w-full sm:w-56 bg-background border border-input px-2.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary"
    />
  );
}

export const textMatch = (row, q, keys) => {
  const s = (q || "").trim().toLowerCase();
  if (!s) return true;
  return keys.some((k) => String(row?.[k] ?? "").toLowerCase().includes(s));
};

/* ---------- meta (dropdown options) ---------- */
let _metaCache = null;
export function useAggMeta(refreshKey = 0) {
  const [meta, setMeta] = useState(_metaCache);
  useEffect(() => {
    let live = true;
    api.get("/agg/meta").then((r) => { _metaCache = r.data; if (live) setMeta(r.data); }).catch(() => {});
    return () => { live = false; };
  }, [refreshKey]);
  return meta;
}
export const clearAggMeta = () => { _metaCache = null; };

export const CHART_COLORS = ["#FF5A1F", "#3B82F6", "#10B981", "#A855F7", "#F59E0B", "#EC4899", "#14B8A6", "#64748B"];

/* ---------- tabs + CSV ---------- */
export function TabBar({ tabs, value, onChange, testid = "agg-tabs" }) {
  return (
    <div className="flex gap-1 overflow-x-auto pb-1" data-testid={testid}>
      {tabs.map(([k, l, Icon]) => (
        <button key={k} onClick={() => onChange(k)} data-testid={`${testid}-${k}`}
          className={`shrink-0 px-3 py-2 text-xs font-bold uppercase tracking-wide border inline-flex items-center gap-1.5 transition-colors ${value === k ? "bg-[#0B1220] text-white border-[#0B1220] dark:bg-primary dark:border-primary" : "border-border hover:bg-muted"}`}>
          {Icon && <Icon className="h-3.5 w-3.5" />} {l}
        </button>
      ))}
    </div>
  );
}

export function downloadCsv(filename, rows, columns) {
  const esc = (v) => {
    const s = v === null || v === undefined ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [columns.map(([, label]) => esc(label)).join(",")];
  rows.forEach((r) => lines.push(columns.map(([k, , fn]) => esc(fn ? fn(r) : r[k])).join(",")));
  const blob = new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function CsvButton({ onClick, testid = "csv-export" }) {
  return (
    <button data-testid={testid} onClick={onClick} className="h-8 px-2.5 border border-border text-xs font-bold uppercase tracking-wide inline-flex items-center gap-1.5 hover:bg-muted">
      ⬇ Export CSV
    </button>
  );
}

export function ScoreBar({ value, testid }) {
  const v = Math.max(0, Math.min(100, Number(value || 0)));
  const c = v >= 70 ? "bg-emerald-500" : v >= 45 ? "bg-amber-500" : "bg-rose-500";
  return (
    <div className="flex items-center gap-2 min-w-[110px]" data-testid={testid}>
      <div className="flex-1 h-1.5 bg-muted"><div className={`h-full ${c}`} style={{ width: `${v}%` }} /></div>
      <span className="font-mono-num font-bold w-9 text-right">{v.toFixed(0)}</span>
    </div>
  );
}
